import {
  MarketingPlanState,
  type OpportunitiesQuery,
  type PoliciesListQuery,
  type QuotesQuery,
  useOpportunitiesQuery,
  usePoliciesListQuery,
  useQuotesQuery,
} from "@qw/graphql";
import { useEffect, useMemo, useRef } from "react";

type InsuredScopedRow = {
  insuredId: string;
};

type InsuredId = string | null | undefined;
type Opportunities = OpportunitiesQuery["opportunities"];
type Opportunity = Opportunities[number];
type Policies = PoliciesListQuery["policies"];
type Policy = Policies[number];
type Quotes = QuotesQuery["quotes"];
type Quote = Quotes[number];

interface CurrentInsuredRowsArgs<Row extends InsuredScopedRow> {
  insuredId: InsuredId;
  rows?: readonly Row[];
  queryLoading: boolean;
  queryHasData: boolean;
  skip?: boolean;
}

interface UseInsuredOpportunitiesOptions {
  includeDeleted?: boolean;
  skip?: boolean;
}

interface UseInsuredPoliciesListOptions {
  active?: boolean;
  skip?: boolean;
}

const useCurrentInsuredRows = <Row extends InsuredScopedRow>({
  insuredId,
  rows,
  queryLoading,
  queryHasData,
  skip = false,
}: CurrentInsuredRowsArgs<Row>) => {
  const lastSettledInsuredIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!skip && !queryLoading && queryHasData && insuredId) {
      lastSettledInsuredIdRef.current = insuredId;
    }
  }, [insuredId, queryHasData, queryLoading, skip]);

  const currentRows = useMemo(
    () => (insuredId ? (rows ?? []).filter((row) => row.insuredId === insuredId) : []),
    [insuredId, rows]
  );

  if (!insuredId || skip) {
    return { rows: [], loading: false };
  }

  const hasOutOfScopeRows = Boolean(rows?.some((row) => row.insuredId !== insuredId));
  const hasInScopeRows = Boolean(rows?.some((row) => row.insuredId === insuredId));
  const hasSettledCurrentScope = lastSettledInsuredIdRef.current === insuredId;

  return {
    rows: currentRows,
    loading: queryLoading && (!queryHasData || hasOutOfScopeRows || (!hasInScopeRows && !hasSettledCurrentScope)),
  };
};

export const useInsuredOpportunities = (
  insuredId: InsuredId,
  { includeDeleted = false, skip = false }: UseInsuredOpportunitiesOptions = {}
) => {
  const shouldSkip = skip || !insuredId;
  const { data, loading, refetch } = useOpportunitiesQuery({
    variables: {
      input: {
        insuredId: insuredId ?? "",
        ...(includeDeleted ? { includeDeleted } : {}),
      },
    },
    skip: shouldSkip,
    fetchPolicy: "cache-and-network",
  });
  const { rows: opportunities, loading: opportunitiesLoading } = useCurrentInsuredRows<Opportunity>({
    insuredId,
    rows: data?.opportunities,
    queryLoading: loading,
    queryHasData: Boolean(data),
    skip: shouldSkip,
  });

  return {
    opportunities,
    opportunitiesLoading,
    refetch,
  };
};

export const useInsuredActiveOpportunities = (insuredId: InsuredId, options?: UseInsuredOpportunitiesOptions) => {
  const { opportunities, opportunitiesLoading, refetch } = useInsuredOpportunities(insuredId, options);
  const activeOpportunities = useMemo(
    () => opportunities.filter((opportunity) => opportunity.state !== MarketingPlanState.Complete),
    [opportunities]
  );

  return {
    activeOpportunities,
    opportunities,
    opportunitiesLoading,
    refetch,
  };
};

export const useInsuredQuotes = (insuredId: InsuredId) => {
  const shouldSkip = !insuredId;
  const { data, loading } = useQuotesQuery({
    variables: { input: { insuredId: insuredId ?? "" } },
    skip: shouldSkip,
    fetchPolicy: "cache-and-network",
  });
  const { rows: quotes, loading: quotesLoading } = useCurrentInsuredRows<Quote>({
    insuredId,
    rows: data?.quotes,
    queryLoading: loading,
    queryHasData: Boolean(data),
    skip: shouldSkip,
  });

  return { quotes, quotesLoading };
};

export const useInsuredPoliciesList = (
  insuredId: InsuredId,
  { active, skip = false }: UseInsuredPoliciesListOptions = {}
) => {
  const shouldSkip = skip || !insuredId;
  const { data, loading } = usePoliciesListQuery({
    variables: {
      input: {
        insuredId: insuredId ?? "",
        ...(active === undefined ? {} : { active }),
      },
    },
    skip: shouldSkip,
    fetchPolicy: "cache-and-network",
  });
  const { rows: policies, loading: policiesLoading } = useCurrentInsuredRows<Policy>({
    insuredId,
    rows: data?.policies,
    queryLoading: loading,
    queryHasData: Boolean(data),
    skip: shouldSkip,
  });

  return { policies, policiesLoading };
};

export const useInsuredActivePolicies = (insuredId: InsuredId) => {
  const { policies, policiesLoading } = useInsuredPoliciesList(insuredId, { active: true });

  return { activePolicies: policies, activePoliciesLoading: policiesLoading };
};
